/* eslint-disable @typescript-eslint/no-explicit-any */
import { useParams, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { 
  useFetchDebtSecurityDetail,
  useUpdateDebtSecurity, 
  useDeleteDebtSecurity,
} from '@/hooks/securities/debt/useDebtSecurities.ts';
import { useState } from 'react';
import { toast } from 'sonner';
import { 
  PageHeaderSkeleton, 
  AccountCardSkeleton 
} from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, 
  AlertCircle, 
  Edit, 
  Trash2,
  ArrowUpCircle,
  ArrowDownCircle,
  Calendar
} from 'lucide-react';
import { DebtSecurityForm } from '@/components/debt-security-form/DebtSecurityForm';
import { CouponScheduleSection } from '@/components/coupon-schedule-section/CouponScheduleSection';
import { format } from 'date-fns';
import { SecuritiesErrorState } from '@/components/securities-error-state/SecuritiesErrorState';
import {DebtSecurity} from "@/integrations/supabase/debt.models.ts";

/**
 * DebtSecurityDetailPage component displays detailed information for a single debt security.
 * Accessible at /investments/securities/debt-securities/:id
 */
export default function DebtSecurityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Dialog state
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch debt security detail
  const { 
    data: debtSecurity,
    isLoading, 
    error,
    refetch,
  } = useFetchDebtSecurityDetail(id!);

  // Mutation hooks
  const updateDebtSecurity = useUpdateDebtSecurity();
  const deleteDebtSecurity = useDeleteDebtSecurity();

  // Handler functions
  const handleBack = () => {
    navigate('/investments/securities/debt-securities');
  };

  const handleEdit = () => {
    setEditFormOpen(true);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleSaveDebtSecurity = async (data: any) => {
    try {
      await updateDebtSecurity.mutateAsync({
        id: id!,
        ...data,
      });
      toast.success('Debt security updated successfully');
      setEditFormOpen(false);
    } catch (error) {
      console.error('Error updating debt security:', error);
      toast.error('Failed to update debt security');
    }
  };

  const handleCancelEdit = () => {
    setEditFormOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDebtSecurity.mutateAsync(id!);
      toast.success('Debt security deleted successfully');
      navigate('/investments/securities/debt-securities');
    } catch (error) {
      console.error('Error deleting debt security:', error);
      toast.error('Failed to delete debt security');
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  const formatCurrency = (amount: number, currency: string) => {
    const formatted = amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currency} ${formatted}`;
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  // Show loading skeletons while data is loading
  if (isLoading) {
    return (
      <ErrorBoundary
        onReset={() => {
          window.location.reload();
        }}
      >
        <div className="space-y-6" aria-busy="true" aria-live="polite">
          <PageHeaderSkeleton />
          <div className="space-y-4">
            <AccountCardSkeleton />
            <AccountCardSkeleton />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // Show error state
  if (error || !debtSecurity) {
    return (
      <ErrorBoundary onReset={() => { window.location.reload(); }}>
        <div className="space-y-6">
          <SecuritiesErrorState
            message="Failed to load debt security details. Please try again."
            onRetry={() => refetch()}
            action={
              <Button onClick={handleBack} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                Back to Debt Securities
              </Button>
            }
          />
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      onReset={() => {
        window.location.reload();
      }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleBack}
              variant="ghost"
              size="icon"
              aria-label="Back to debt securities"
              className="min-h-[44px] min-w-[44px]"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {debtSecurity.issuer?.name || 'Unknown Issuer'}
              </h1>
              <p className="text-sm text-muted-foreground sm:text-base">
                ISIN: {debtSecurity.isin}
              </p>
            </div>
          </div>
          <div className="flex gap-2" role="group" aria-label="Debt security actions">
            <Button
              onClick={handleEdit}
              variant="outline"
              aria-label="Edit debt security"
              className="min-h-[44px]"
            >
              <Edit className="mr-2 h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
            <Button
              onClick={handleDelete}
              variant="outline"
              aria-label="Delete debt security"
              className="min-h-[44px]"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Face Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(debtSecurity.face_value, debtSecurity.currency)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Coupon Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {debtSecurity.coupon_rate.toFixed(2)}%
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Days Until Maturity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center gap-2">
                {debtSecurity.daysUntilMaturity}
                <Calendar className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Quantity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {debtSecurity.quantity.toLocaleString(undefined, { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 6 
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bond Characteristics */}
        <Card>
          <CardHeader>
            <CardTitle>Bond Characteristics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">ISIN</p>
                <p className="text-lg font-medium">{debtSecurity.isin}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Issuer</p>
                <p className="text-lg font-medium">
                  {debtSecurity.issuer?.name || 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Issue Number</p>
                <p className="text-lg font-medium">{debtSecurity.issue_number}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Nominal Price</p>
                <p className="text-lg font-medium">
                  {formatCurrency(debtSecurity.nominal_price, debtSecurity.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Tenor</p>
                <p className="text-lg font-medium">{debtSecurity.tenor}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Maturity Date</p>
                <p className="text-lg font-medium">
                  {formatDate(debtSecurity.maturity_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Currency</p>
                <p className="text-lg font-medium">{debtSecurity.currency}</p>
              </div>
              {debtSecurity.notes && (
                <div className="md:col-span-2 lg:col-span-3">
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-lg font-medium">{debtSecurity.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Coupon Schedule */}
        <CouponScheduleSection
          issueDate={debtSecurity.issue_date}
          maturityDate={debtSecurity.maturity_date}
          couponRate={debtSecurity.coupon_rate}
          faceValue={debtSecurity.face_value}
          currency={debtSecurity.currency}
          isLoading={false}
          bond={debtSecurity}
        />

        {/* Transaction History */}
        <Card>
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
          </CardHeader>
          <CardContent>
            {debtSecurity.transactions && debtSecurity.transactions.length > 0 ? (
              <div className="space-y-3" role="list" aria-label="Transaction list">
                {debtSecurity.transactions.map((transaction: any) => {
                  const isDeposit = transaction.transaction_type === 'deposit';
                  const Icon = isDeposit ? ArrowDownCircle : ArrowUpCircle;
                  const iconColor = isDeposit ? 'text-green-600' : 'text-blue-600';

                  return (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                      role="article"
                      onClick={() => navigate('/transactions/securities/' + transaction.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={`h-8 w-8 ${iconColor}`} aria-hidden="true" />
                        <div>
                          <p className="font-medium capitalize">{transaction.transaction_type}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(transaction.transaction_date)}
                          </p>
                          {transaction.notes && (
                            <p className="text-sm text-muted-foreground">{transaction.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No transactions recorded yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editFormOpen} onOpenChange={setEditFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="edit-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>Edit Debt Security</DialogTitle>
              <DialogDescription id="edit-dialog-description">
                Update the details of your bond holding.
              </DialogDescription>
            </DialogHeader>
            <DebtSecurityForm
              debtSecurity={debtSecurity}
              onSave={handleSaveDebtSecurity}
              onCancel={handleCancelEdit}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent 
            className="w-[95vw] sm:w-full sm:max-w-[500px]"
            aria-describedby="delete-dialog-description"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription id="delete-dialog-description">
                This action cannot be undone. This will permanently delete the debt security
                and all associated transaction history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel 
                onClick={handleCancelDelete} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Cancel deletion"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                aria-label="Confirm deletion of debt security"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
